// src/components/DiseaseRankingTable.tsx (疾患深掘り Phase 1)
// DPC 6 桁の疾患別 KPI ランキング表。
//   - デフォルトソート: 1 日 (粗利|入院) 単価 降順 (ユーザ主目的)
//   - 列クリックでソート切替
//   - 行クリック → setDpc6(row.dpc6) で sidebar の DPC 6 桁 input を更新 (drill 用)
//   - DPC II %: ≥80% 緑 / 60-80% 黄 / <60% 赤
//   - サイドバー「収入の基準」(粗利 / 入院収入・全タブ共通 revenueBasis) で 1日単価 + 合計 列を mode 切替
import { useMemo } from "react";
import { useFilters, applyDpcRowSelection, selectedDpcAt } from "../store";
import { dpcLevelMeta } from "../lib/dpcLevels";
import { useDpcAgg } from "../lib/useDpcAgg";
import { useDiseaseRanking } from "../lib/useDiseaseRanking";
import { CsvButton } from "./CsvButton";
import { fmtInt } from "../lib/format";
import { useSort, SortTh } from "../lib/useSort";
import { procHover } from "../lib/dpcHover";
import type { DiseaseRankingRow } from "@shared/api";
import { gpText } from "../lib/gpMode";

const yenPerDayFmt = new Intl.NumberFormat("ja-JP", {
  style: "currency",
  currency: "JPY",
  maximumFractionDigits: 0,
});

function fmtYenPerDay(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return yenPerDayFmt.format(v);
}

function fmtDays(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(1)}日`;
}

function fmtPct1(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

/** DPC II % の色 (text-* Tailwind class) */
function dpc2ColorClass(pct: number): string {
  if (pct >= 0.80) return "text-emerald-700 font-semibold";
  if (pct >= 0.60) return "text-amber-600";
  return "text-red-600";
}

// [中央値 2026-07-22 ユーザ指示] 1日単価列は「患者1人ずつの1日単価」の中央値。
//   旧定義 (Σ収入 ÷ Σ入院延日数) は、長期入院の1例が分母を大きく食って単価を低い方へ
//   引っ張るため、疾患の代表値としては外れ値に弱かった。列見出しの「※」がこの注記を指す。
const UNIT_MEDIAN_NOTE =
  gpText("※中央値: 患者(急性期エピソード)1人ずつの1日単価 (その患者の収入または粗利 ÷ その患者の入院日数) を出し、その中央値。")
  + "\n長期入院の外れ値に引っ張られないようにするため、合計÷合計 (加重平均) ではありません。"
  + gpText("\nしたがって 1日単価 × 入院延日数 は 収入・粗利の合計には一致しません。");

/** 千円単位フォーマット (`12,345 千円` 形式) */
function fmtThousandYen(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${Math.round(v / 1000).toLocaleString("ja-JP")}`;
}

export function DiseaseRankingTable() {
  const f = useFilters();
  const { data, isLoading, error, isPlaceholderData } = useDiseaseRanking();

  // 指標 mode 切替: 粗利 / 入院収入
  const isProfit  = f.revenueBasis === "粗利";
  const unitLabel  = isProfit ? gpText("1日粗利単価") : "1日入院単価";
  const totalLabel = isProfit ? gpText("粗利(千円)")  : "入院収入(千円)";
  // [桁切替 2026-06-26] 手術名=10桁以上 / 処置1名=11桁以上 / 処置2名=12桁以上。
  //   [2026-09-28 ユーザ指示] DPCコード列を 1 列目に戻した (7/8 桁の分岐が見分けられないため)。DPCⅡ以内/超 単価は削除済。
  const showOpe   = f.dpcLevel >= 10;
  const showProc1 = f.dpcLevel >= 11;
  const showProc2 = f.dpcLevel >= 12;
  const show13    = f.dpcLevel >= 13;   // [2026-09-28] 副傷病(13桁目) 列
  const show14    = f.dpcLevel === 14;   // 完全(14桁): 重症度等(14桁目) 列
  const lvlMeta   = dpcLevelMeta(f.dpcLevel);
  const agg       = useDpcAgg();   // [2026-09-28] 件数・金額の 期間累計/月平均 (サイドバーで切替)
  // [2026-09-28 ユーザ指示] いま見ている階層で分かれる列 (処置2別なら処置2分岐) を薄く強調する。
  //   行の選択 (🎯 の緑枠) と紛れないよう、背景は選択行より淡く・文字は通常の太さ + 濃色。
  const ACTIVE_COL: Record<number, string> = {
    6: "疾患名", 7: "病態等", 8: "年齢等", 10: "手術名", 11: "処置1名", 12: "処置2名", 13: "副傷病", 14: "重症度",
  };
  const activeKey = ACTIVE_COL[f.dpcLevel] ?? "";
  const hl = (key: string) => key === activeKey ? "bg-emerald-50/80 !text-slate-900 font-medium" : "";

  // 2026-05-27 ユーザ指示の列順:
  //   1.疾患コード 2.疾患名 3.症例数 4.平均在院日数 5.入院患者延数 6.DPCⅡ以内率
  //   7.1日(粗利|入院)単価 8.(DPCⅡ以内) 9.(DPCⅡ超) 10.(粗利|入院収入)(千円)
  // 各列に並べ替えキー(get)を持たせて SortTh に渡す。動的な単価/合計列は
  //   固定 sortKey ("_unit"/"_total") + mode 依存 get で、粗利↔収入 切替でも同じ論理列でソート。
  //   sortable:false の列は get 不要 (現在は全列ソート可)。
  // [2026-09-28 ユーザ指示] 7桁目(病態等)/8桁目(年齢・JCS等) の列は、表示中の行のどれかがその桁で
  //   分岐しているときだけ出す (大半の疾患は x = 分岐なし で、常に出すと空の列が 2 本増えるだけ)。
  //   これが無いと 10 桁表示で「同じ疾患・同じ手術分類」が 2 行並んで見えていた (例: くも膜下出血の JCS)。
  const rows0 = data?.rows ?? [];
  const show7 = f.dpcLevel >= 7 && rows0.some((r) => !!r["病態等"]);
  const show8 = f.dpcLevel >= 8 && rows0.some((r) => !!r["年齢等"]);

  const COLUMNS: {
    key: string; label: string; align?: "left" | "right"; title?: string; sortable?: boolean;
    get?: (r: DiseaseRankingRow) => unknown;
  }[] = useMemo(() => [
    // [2026-09-28 ユーザ指示] 1 列目に DPC コード。表示階層で増えた桁を太字にする。
    { key: "_code",     label: "DPC\nコード", align: "left" as const, title: "表示階層までの DPC コード。太字がこの階層で分かれる桁 (x = 分岐なし)。", get: (r) => r.dpc6 },
    { key: "疾患名",    label: "疾患",     align: "left" as const, get: (r) => r["疾患名"] },
    ...(show7 ? [{ key: "病態等", label: "病態等\n7桁", align: "left" as const, title: "DPC 7 桁目 = 病態等分類。空欄 = この疾患では分岐なし (x)。", get: (r: DiseaseRankingRow) => r["病態等"] }] : []),
    ...(show8 ? [{ key: "年齢等", label: "年齢等\n8桁", align: "left" as const, title: "DPC 8 桁目 = 年齢・出生時体重・JCS 等 (例: くも膜下出血の JCS10未満/以上)。空欄 = この疾患では分岐なし (x)。", get: (r: DiseaseRankingRow) => r["年齢等"] }] : []),
    ...(showOpe   ? [{ key: "手術名",  label: "手術\n分類",  align: "left" as const, title: "DPC分岐の手術分類名。実際の手術Kコードはセルにカーソルを当てると表示。", get: (r: DiseaseRankingRow) => r["手術名"] }] : []),
    ...(showProc1 ? [{ key: "処置1名", label: "処置1\n分岐", align: "left" as const, title: "DPC「手術・処置等1」の分岐名。分岐に対応する候補Kコードはセルにカーソル。", get: (r: DiseaseRankingRow) => r["処置1名"] }] : []),
    ...(showProc2 ? [{ key: "処置2名", label: "処置2\n分岐", align: "left" as const, title: "DPC「手術・処置等2」の分岐名。分岐に対応する候補Kコードはセルにカーソル。", get: (r: DiseaseRankingRow) => r["処置2名"] }] : []),
    ...(show13 ? [{ key: "副傷病", label: "副傷病\n13桁", align: "left" as const, title: "DPC 13 桁目 = 定義副傷病の有無。カーソルで実績の副傷病 (医療資源病名2)。", get: (r: DiseaseRankingRow) => r["副傷病"] }] : []),
    ...(show14 ? [{ key: "重症度", label: "重症度\n14桁", align: "left" as const, title: "DPC 14 桁目 = 重症度等。", get: (r: DiseaseRankingRow) => r["重症度"] }] : []),
    { key: "n_cases",   label: "退院数", title: "急性期を出た入院数（急性期からの退院 or 回復期へ転棟）。病院からの退院数とは一致しません。サイドバー「件数・金額の表示」で期間累計/月平均を切り替え。", get: (r) => r.n_cases },
    { key: "alos",      label: "入院日数\n中央値", title: "急性期在院日数（急性期最終日 − 入院日 + 1）の中央値。", get: (r) => r.alos },
    // [2026-09-28 ユーザ指示] 入院延日数 列は削除 (横スクロールなしで収めるため)。CSV には残す。
    { key: "dpc2_pct",  label: "DPCⅡ\n以内率", title: "期間I・IIの在院日数 ÷ 全在院日数（日数ベース。退院患者の件数割合ではありません）。", get: (r) => r.dpc2_pct },
    { key: "_unit",     label: `${unitLabel.replace(/単価$/, "\n単価")} ※`, title: UNIT_MEDIAN_NOTE, get: (r) => (isProfit ? r.gp_per_day : r.price_per_day) },
    { key: "_total",    label: totalLabel.replace("(千円)", "\n(千円)"), get: (r) => (isProfit ? r.gp_total : r.revenue_total) },
  ], [unitLabel, totalLabel, isProfit, showOpe, showProc1, showProc2, show7, show8, show13, show14]);

  const rows = data?.rows ?? [];
  // 月平均化用: 期間月数 (months) ではなく 実効月数 (monthsEffective) で割る。
  // 直近月が会計未確定 (収益 NULL) の場合、 期間月数で割ると月平均が薄まるため
  // 「収益データのある月数」 で割ることで「いつもの月のペース」 を反映する。
  // fallback は 1 で「割らない」 安全側 (server 応答前 or 異常時)。
  const months          = data?.months ?? 1;             // 期間月数 (表示・補助)
  const monthsEffective = data?.monthsEffective ?? months; // 月平均の分母

  // 初期表示 = 従来のデフォルトソート (粗利(千円)|入院収入(千円) 降順・mode 追従)。
  //   共通フック useSort は key 未設定時この原順を保つ → ヘッダクリックで並べ替え可能。
  //   mode トグルで baseRows の identity が変わり、useSort の原順も新 mode に追従する。
  const baseRows = useMemo(() => {
    const key = isProfit ? "gp_total" : "revenue_total";
    return [...rows].sort((a, b) => (b[key] as number) - (a[key] as number));
  }, [rows, isProfit]);

  const s = useSort(baseRows);
  const sortedRows = s.rows;

  const handleRowClick = (row: DiseaseRankingRow) => {
    // [連打ガード 2026-09-28] 読み込み中は前の階層の行が仮表示されている (placeholderData)。
    //   その行を押すと、いまの階層とずれた行で絞り込みが走るので受け付けない。
    if (isPlaceholderData) return;
    // [次の分岐へ 2026-09-28 ユーザ指示] 遷移先は server が先読みした next_level
    //   (分岐の無い階層は飛ばす。例: くも膜下出血は 疾患別 → 8桁目(JCS) → 手術別)。
    // [drill-down廃止 2026-06-27] 主表のレベル切替+行クリックで selectedDpc10/11/12 をセット
    //   → 下の表6/7/8(kcode)・DPC包括点数(tensu)を絞る。dpc6 は常に先頭6桁(親疾患)。
    // [絞り込み保持 2026-07-17] 処理は store の applyDpcRowSelection に共通化
    //   (粗利分析 DPC別と共有)。同じ行の再クリックは「その階層以下」だけ解除。
    applyDpcRowSelection(row.dpc6, f.dpcLevel, row.next_level ?? null);
  };

  if (error) {
    return (
      <div className="card p-4 text-sm text-red-700">
        <div className="font-semibold mb-1">疾患ランキング取得エラー</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    );
  }

  return (
    <section className="card p-4 print-flow">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-base font-semibold">
          🔬 {lvlMeta.tab}ランキング
          {" "}(DPC {f.dpcLevel} 桁) — 件数・{isProfit ? gpText("粗利") : "収入"} は <span className="text-emerald-700">{agg.label}</span>
        </h2>
        {/* [中央値 2026-07-22 ユーザ指示] 1日単価の定義が変わったことを画面上でも明示 */}
        <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 cursor-help"
              title={UNIT_MEDIAN_NOTE}>
          ※ {unitLabel} = 患者1人ずつの単価の中央値
        </span>
        {data && (
          <span className="perf-pill">
            SQL {data.sqlMs} ms / {rows.length} 件 / 期間内 {fmtInt(data.totalCases)} 急性期エピソード
          </span>
        )}
        {data?.excludedReasons && (data.excludedReasons.未分類 > 0 || data.excludedReasons.収益欠損 > 0) && (
          <span className="text-[10px] text-slate-400">
            (除外: 未分類 {data.excludedReasons.未分類} / 収益欠損 {data.excludedReasons.収益欠損})
          </span>
        )}
        {isLoading && <span className="text-xs text-slate-400">loading…</span>}
        <CsvButton
          rows={sortedRows.map((r) => {
            // mode に応じて列名・値を切替 (粗利 mode なら粗利列、 収入 mode なら収入列)
            const unitVal     = isProfit ? r.gp_per_day : r.price_per_day;
            const totalVal    = isProfit ? r.gp_total   : r.revenue_total;
            // [中央値 2026-07-22] CSV も列名で定義がわかるように (中央値であることを列名に入れる)
            const unitColLabel    = isProfit ? gpText("1日粗利単価_円_中央値") : "1日入院単価_円_中央値";
            const totalColLabel   = isProfit ? gpText("月平均粗利_千円")  : "月平均入院収入_千円";
            const periodTotalLabel= isProfit ? gpText("期間累計_粗利_円") : "期間累計_入院収入_円";
            return {
              DPCコード: r.dpc6,
              疾患名: r["疾患名"],
              ...(show7 ? { "病態等_7桁目": r["病態等"] } : {}),
              ...(show8 ? { "年齢JCS等_8桁目": r["年齢等"] } : {}),
              ...(showOpe   ? { 手術名:  r["手術名"]  } : {}),
              ...(showProc1 ? { 処置1名: r["処置1名"], 処置1候補K: r["処置1候補K"] } : {}),
              ...(showProc2 ? { 処置2名: r["処置2名"], 処置2候補K: r["処置2候補K"] } : {}),
              ...(show13 ? { 副傷病_13桁目: r["副傷病"] ?? "" } : {}),
              ...(show14 ? { 重症度等_14桁目: r["重症度"] ?? "" } : {}),
              MDC2: r.mdc2,
              退院数: r.n_cases,
              入院日数中央値: r.alos,
              入院延日数: r.los_total,
              "DPCⅡ以内率": r.dpc2_pct,
              [unitColLabel]:     unitVal,
              [totalColLabel]:    Math.round(totalVal / monthsEffective / 1000),
              [periodTotalLabel]: totalVal,
              期間月数: months,
              実効月数: monthsEffective,
            };
          })}
          filename={`disease_ranking_${f.asOfMonth.slice(0,7)}_${isProfit ? gpText("粗利") : "収入"}${f.focusedDept ? "_" + f.focusedDept : ""}`}
          style={{ marginLeft: "auto" }}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="text-[13px] border-collapse w-full">
          <thead>
            <tr className="bg-slate-100 sticky top-0">
              {COLUMNS.map((c) => {
                const sortable = c.sortable !== false && c.get;
                const cls = [
                  "px-1 py-1.5 border-b border-slate-200 whitespace-pre leading-tight align-bottom text-xs",
                  c.align === "left" ? "text-left" : "text-right",
                  c.key === activeKey ? "!bg-emerald-100 text-emerald-900 border-b-2 !border-b-emerald-500" : "",
                ].join(" ");
                if (!sortable) {
                  return (
                    <th key={c.key} className={`${cls} select-none`} title={c.title}>
                      {c.label}
                    </th>
                  );
                }
                return (
                  <SortTh
                    key={c.key}
                    s={s}
                    sortKey={c.key}
                    get={(r) => c.get!(r as DiseaseRankingRow)}
                    className={`${cls} hover:bg-slate-200`}
                    // 列の定義説明 (c.title) があればホバーで出す。無ければ既定の「クリックで昇順/降順」。
                    title={c.title ? `${c.title}\n(クリックで昇順/降順)` : undefined}
                  >
                    {c.label}
                  </SortTh>
                );
              })}
            </tr>
          </thead>
          <tbody className={isPlaceholderData ? "opacity-40 pointer-events-none transition-opacity" : "transition-opacity"}>
            {sortedRows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={COLUMNS.length} className="text-center text-slate-400 py-6">
                  該当データがありません (filter を見直してください)
                </td>
              </tr>
            )}
            {sortedRows.map((r) => {
              const isFocused = f.dpc6 != null && f.dpc6 === r.dpc6.slice(0, 6)
                && selectedDpcAt(f, f.dpcLevel) === r.dpc6;
              const nextMeta = r.next_level != null && r.next_level !== f.dpcLevel ? dpcLevelMeta(r.next_level) : null;
              const rowHint = isFocused ? "クリックで選択解除"
                : r.dpc6.length < f.dpcLevel ? "14桁未確定 (DPC コーディング前) の症例。この階層では絞り込めません"
                : nextMeta ? `クリックで絞り込み →「${nextMeta.tab}」(${nextMeta.sub}) へ（間の分岐しない桁は飛ばします。下の ICD・実績K も連動）`
                : "クリックで絞り込み（これ以上の分岐はありません。下の ICD・実績K・包括点数が連動）";
              const seg0 = dpcLevelMeta(f.dpcLevel).segStart;
              const unitVal    = isProfit ? r.gp_per_day : r.price_per_day;
              const totalVal   = isProfit ? r.gp_total   : r.revenue_total;
              return (
                <tr
                  key={r.dpc6}
                  className={[
                    "border-b border-slate-100 cursor-pointer",
                    isFocused ? "bg-emerald-100 outline outline-2 outline-emerald-400 font-semibold"
                              : "hover:bg-slate-50",
                  ].join(" ")}
                  onClick={() => handleRowClick(r)}
                  title={rowHint}
                >
                  {/* [2026-09-28] DPC コード。この階層で増えた桁を太字 (x = 分岐なし は薄く) */}
                  <td className="text-left px-1 py-1.5 font-mono text-[11px] tracking-tight tabular-nums whitespace-nowrap">
                    <span className="text-slate-400">{r.dpc6.slice(0, seg0)}</span>
                    <span className={/^[xX]+$/.test(r.dpc6.slice(seg0)) ? "text-slate-400" : "font-bold text-slate-800"}>{r.dpc6.slice(seg0)}</span>
                    {nextMeta && <span className="ml-1 text-[10px] text-emerald-600" aria-hidden>›</span>}
                  </td>
                  {/* 列順 (ユーザ指示 2026-05-27):
                      疾患コード / 疾患名 / 新規受持件数 / 平均在院日数 / 入院患者延数 /
                      DPCⅡ以内率 / 1日(粗利|入院)単価 / (DPCⅡ以内) / (DPCⅡ超) /
                      (粗利|入院収入)(千円) */}
                  {/* 各列ホバーで候補コード(処置はK候補)。疾患セルのホバーには実績ICD。 */}
                  <td
                    className={`text-left px-1 py-1.5 min-w-[6rem] underline decoration-dotted decoration-slate-300 cursor-help ${hl("疾患名")}`}
                    title={[
                      `DPCコード: ${r.dpc6}`,
                      `疾患: ${r["疾患名"]}`,
                      r.mdc2 ? `MDC: ${r.mdc2}` : "",
                      r["副傷病"] ? `副傷病: ${r["副傷病"]}` : "",
                      r["重症度"] ? `重症度: ${r["重症度"]}` : "",
                      r.icd_list ? `── 実績ICD病名 ──\n${r.icd_list.split(" / ").join("\n")}` : "",
                    ].filter(Boolean).join("\n")}
                  >
                    {isFocused && "🎯 "}{r["疾患名"]}
                  </td>
                  {show7 && (
                    <td className={`text-left px-1 py-1.5 text-xs text-slate-700 min-w-[3.5rem] ${hl("病態等")}`}>{r["病態等"] || <span className="text-slate-300">—</span>}</td>
                  )}
                  {show8 && (
                    <td className={`text-left px-1 py-1.5 text-xs text-slate-700 min-w-[3.5rem] ${hl("年齢等")}`}>{r["年齢等"] || <span className="text-slate-300">—</span>}</td>
                  )}
                  {showOpe && (
                    <td className={`text-left px-1 py-1.5 min-w-[6rem] text-xs text-slate-600 cursor-help ${hl("手術名")}`}
                        title={[
                          `手術分類: ${r["手術名"]}（DPC分岐の分類名。実際の手術Kは下記）`,
                          r.ope_list ? `── 実績手術K ──\n${r.ope_list.split(" / ").join("\n")}` : "",
                        ].filter(Boolean).join("\n")}>
                      {r["手術名"]}
                    </td>
                  )}
                  {showProc1 && (
                    <td className={`text-left px-1 py-1.5 min-w-[3.5rem] text-xs text-slate-600 cursor-help ${hl("処置1名")}`}
                        title={procHover({ n: 1, branchName: r["処置1名"], jisseki: r["処置1実績"],
                          heishi: r["処置1実績_併施"], candK: r["処置1候補K"], candName: r["処置1候補名"] })}>
                      {r["処置1名"]}
                    </td>
                  )}
                  {showProc2 && (
                    <td className={`text-left px-1 py-1.5 min-w-[3.5rem] text-xs text-slate-600 cursor-help ${hl("処置2名")}`}
                        title={procHover({ n: 2, branchName: r["処置2名"], jisseki: r["処置2実績"],
                          heishi: r["処置2実績_併施"], candK: r["処置2候補K"], candName: r["処置2候補名"] })}>
                      {r["処置2名"]}
                    </td>
                  )}
                  {show13 && (
                    <td className={`text-left px-1 py-1.5 text-xs text-slate-600 cursor-help min-w-[3.5rem] ${hl("副傷病")}`}
                        title={[
                          `副傷病(13桁目 = ${r.dpc6.slice(12, 13) || "—"}): ${r["副傷病"] || "—"}`,
                          r["副傷病実績"] ? `── 実績副傷病(医療資源病名2・件数降順) ──\n${r["副傷病実績"].split(" / ").join("\n")}` : "（この群に実績副傷病の記録なし）",
                        ].filter(Boolean).join("\n")}>
                      {r["副傷病"] || <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  {show14 && (
                    <td className={`text-left px-1 py-1.5 text-xs text-slate-600 min-w-[3.5rem] ${hl("重症度")}`}
                        title={`重症度等(14桁目 = ${r.dpc6.slice(13, 14) || "—"})`}>
                      {r["重症度"] || <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  <td
                    className="text-right px-1 py-1.5 tabular-nums whitespace-nowrap"
                    title={`${agg.note(r.n_cases, "件")} (急性期を出た入院数。急性期からの退院 or 回復期へ転棟)`}
                  >{agg.fmtCount(r.n_cases)}</td>
                  <td className="text-right px-1 py-1.5 tabular-nums whitespace-nowrap">{fmtDays(r.alos)}</td>
                  <td className={`text-right px-1 py-1.5 tabular-nums whitespace-nowrap ${dpc2ColorClass(r.dpc2_pct)}`}>
                    {fmtPct1(r.dpc2_pct)}
                  </td>
                  <td className="text-right px-1 py-1.5 tabular-nums whitespace-nowrap font-medium cursor-help"
                      title={UNIT_MEDIAN_NOTE}>
                    {fmtYenPerDay(unitVal)}
                  </td>
                  <td
                    className="text-right px-1 py-1.5 tabular-nums whitespace-nowrap font-medium"
                    title={agg.isAvg ? `期間累計 ${fmtThousandYen(totalVal)} 千円 ÷ データのある ${monthsEffective} ヶ月` : `月平均 ${fmtThousandYen(totalVal / monthsEffective)} 千円`}
                  >{fmtThousandYen(totalVal / agg.div)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
